import Button from 'components/Form/Button';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { RunDunning } from 'services/cobranca';
import { Load } from 'ui/styled';
import { exposeStrapiError } from 'utils';
import Wrapper from '../Wrapper';
import {
  ButtonContainer,
  CounterCard,
  CounterGrid,
  CounterLabel,
  CounterValue,
  LoadingBox,
  NoteBox,
  RunContainer,
  WarningBox,
  WarningText,
  WarningTitle,
} from './styled';

// Limite de envios por chamada, fixado no backend. O que sobrar da fila sai na
// execucao seguinte.
const MAX_PER_RUN = 500;

// Os contadores do `resumo`, na ordem em que aparecem. `always` fica visivel
// mesmo zerado; os demais so aparecem quando tem algo a dizer. `configuracao`
// soma dois contadores do backend que, para o operador, sao o mesmo problema.
const COUNTERS = [
  { key: 'pendentes', label: 'Na fila', always: true },
  {
    key: 'enviados',
    label: 'Enviados',
    previewLabel: 'Serão enviadas',
    always: true,
    highlight: true,
  },
  { key: 'ignorados', label: 'Já enviados' },
  { key: 'cancelados', label: 'Cancelados' },
  { key: 'sem_telefone', label: 'Sem telefone', warning: true },
  { key: 'configuracao', label: 'Configuração', warning: true },
  { key: 'bloqueados_alerta', label: 'Bloqueados por alerta', warning: true },
  { key: 'erros', label: 'Falhas', warning: true },
];

const counterValue = (summary, key) =>
  key === 'configuracao'
    ? (summary?.sem_passo || 0) + (summary?.passos_invalidos || 0)
    : summary?.[key] || 0;

const formatDate = (iso) =>
  /^\d{4}-\d{2}-\d{2}$/.test(iso || '')
    ? iso.split('-').reverse().join('/')
    : 'hoje';

const plural = (n, singular, pluralForm) => (n === 1 ? singular : pluralForm);

// O 403 desta rota tem dois motivos: usuario sem a flag `executar_regua`, ou
// usuario nao admin sem distribuidor. O primeiro ganha a mensagem amigavel; no
// segundo o texto do backend ja explica, entao ele passa adiante.
const handleError = (result) => {
  if (!result) {
    toast.error('Não foi possível falar com o servidor. Tente de novo.');
    return true;
  }
  const status = result?.error?.status;
  const message = `${result?.error?.message || ''}`;
  if (status === 403 && !/distribuidor/i.test(message)) {
    toast.error('Você não tem permissão para executar a régua.');
    return true;
  }
  if (exposeStrapiError(result)) return true;
  if (!result?.resumo) {
    toast.error('A execução da régua não devolveu o resumo.');
    return true;
  }
  return false;
};

// Disparo manual da regua, em dois passos: ao abrir, simula (`simular: true`)
// e mostra o que vai sair; so no confirmar chama de novo para enviar. O botao
// fica desabilitado durante a chamada — o backend ja trava cada disparo, mas
// assim nao nascem duas execucoes concorrentes.
export default function BillingRunDunning({ reload }) {
  const { setModal } = useContext(CoreContext);
  // simulating -> preview -> running -> done
  const [step, setStep] = useState('simulating');
  const [summary, setSummary] = useState(null);

  const close = () => {
    setModal(null);
    if (step === 'done') reload?.();
  };

  const simulate = useCallback(async () => {
    setStep('simulating');
    const result = await RunDunning({ data: { simular: true } });
    if (handleError(result)) {
      setModal(null);
      return;
    }
    setSummary(result.resumo);
    setStep('preview');
  }, [setModal]);

  useEffect(() => {
    simulate();
  }, [simulate]);

  const run = async () => {
    setStep('running');
    const result = await RunDunning({ data: {} });
    if (handleError(result)) {
      setStep('preview');
      return;
    }
    setSummary(result.resumo);
    setStep('done');
    const sent = result.resumo?.enviados || 0;
    toast.success(
      `${sent} ${plural(sent, 'mensagem enviada', 'mensagens enviadas')}.`
    );
  };

  const isResult = step === 'done';
  const toSend = counterValue(summary, 'enviados');
  const hitLimit = isResult && toSend >= MAX_PER_RUN;

  return (
    <Wrapper title="Executar régua" onClose={close}>
      <RunContainer>
        {step === 'simulating' ? (
          <LoadingBox>
            <Load />
            Apurando o que está agendado...
          </LoadingBox>
        ) : (
          <>
            <CounterGrid>
              {COUNTERS.filter(
                (c) => c.always || counterValue(summary, c.key) > 0
              ).map((c) => (
                <CounterCard
                  key={c.key}
                  $highlight={c.highlight}
                  $warning={c.warning}
                >
                  <CounterValue>{counterValue(summary, c.key)}</CounterValue>
                  <CounterLabel>
                    {!isResult && c.previewLabel ? c.previewLabel : c.label}
                  </CounterLabel>
                </CounterCard>
              ))}
            </CounterGrid>

            {isResult ? (
              <NoteBox>
                <strong>Enviado não é entregue.</strong> O WhatsApp aceitou as
                mensagens; a confirmação de entrega (ou o erro) chega pela
                conciliação, de hora em hora, das 08h às 20h, e aparece na aba
                Régua do histórico de cada contrato. Falhas voltam para a fila e
                saem na próxima execução.
                {hitLimit
                  ? ` A execução atingiu o limite de ${MAX_PER_RUN} mensagens: ainda há fila, execute de novo.`
                  : ''}
              </NoteBox>
            ) : (
              <WarningBox>
                <WarningTitle>Confirme antes de enviar</WarningTitle>
                <WarningText>
                  {toSend
                    ? `${toSend} ${plural(
                        toSend,
                        'mensagem de WhatsApp será enviada',
                        'mensagens de WhatsApp serão enviadas'
                      )} agora para clientes reais, com tudo o que está agendado até ${formatDate(
                        summary?.data
                      )}. O envio não pode ser desfeito. Cada execução envia no máximo ${MAX_PER_RUN} mensagens.`
                    : `Não há mensagens a enviar até ${formatDate(
                        summary?.data
                      )}.`}
                </WarningText>
              </WarningBox>
            )}

            <ButtonContainer>
              {isResult ? (
                <Button color="blue" onClick={close} nospace>
                  Fechar
                </Button>
              ) : (
                <>
                  <Button
                    color="lightgrey"
                    onClick={close}
                    disabled={step === 'running'}
                    nospace
                  >
                    Cancelar
                  </Button>
                  <Button
                    color="blue"
                    onClick={run}
                    disabled={step === 'running' || !toSend}
                    nospace
                  >
                    {step === 'running'
                      ? 'Enviando...'
                      : toSend
                        ? `Enviar ${toSend} ${plural(
                            toSend,
                            'mensagem',
                            'mensagens'
                          )}`
                        : 'Nada a enviar'}
                  </Button>
                </>
              )}
            </ButtonContainer>
          </>
        )}
      </RunContainer>
    </Wrapper>
  );
}
